// Who the user is, as src/client/identity.xq: an account that is a key, with no name, mail, phone or
// password. Its secret, 128 random bits, stays in this browser (and on the user's other devices); from
// it comes an Ed25519 key (WebCrypto), which signs the server's challenge whenever the page connects.
// The access code carries the secret to another device: 28 letters and digits in groups of four
// (Crockford's base32, without I, L, O and U), the last 12 bits a check that finds a mistyped one.

const SYMBOLS = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";
/** What an Ed25519 key's PKCS #8 starts with before its 32 bytes (RFC 8410). */
const PKCS8 = [0x30, 0x2e, 0x02, 0x01, 0x00, 0x30, 0x05, 0x06, 0x03, 0x2b, 0x65, 0x70, 0x04, 0x22, 0x04, 0x20];

/** An account's key: its public half, and what signs with it. */
export type Key = { key: Uint8Array; sign(data: Uint8Array): Promise<Uint8Array> };

const sha256 = async (bytes: Uint8Array): Promise<Uint8Array> =>
  new Uint8Array(await crypto.subtle.digest("SHA-256", bytes as BufferSource));
const bitOf = (b: Uint8Array, i: number): number => (b[i >> 3] >> (7 - (i & 7))) & 1;
const text = (s: string): Uint8Array => new TextEncoder().encode(s);

function concat(a: Uint8Array, b: Uint8Array): Uint8Array {
  const all = new Uint8Array(a.length + b.length);
  all.set(a, 0);
  all.set(b, a.length);
  return all;
}

function fromBase64url(s: string): Uint8Array {
  const b = atob(s.replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from(b, (c) => c.charCodeAt(0));
}

/** A new account's secret: 128 random bits. */
export function freshSecret(): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(16));
}

/** The access code of `secret`: its 128 bits and the first 12 of its SHA-256, five a symbol. */
export async function code(secret: Uint8Array): Promise<string> {
  const check = await sha256(secret);
  const bit = (i: number) => (i < 128 ? bitOf(secret, i) : bitOf(check, i - 128));
  let symbols = "";
  for (let k = 0; k < 28; k++) {
    let v = 0;
    for (let j = 0; j < 5; j++) v = (v << 1) | bit(5 * k + j);
    symbols += SYMBOLS[v];
  }
  return symbols.match(/.{4}/g)!.join("-");
}

/** The secret of access code `s` — in any case, with or without dashes and spaces, I and L read as 1,
 * O as 0 — or null if it is not one, or mistyped: its check does not hold. */
export async function parseCode(s: string): Promise<Uint8Array | null> {
  const symbols = [...s.toUpperCase()]
    .filter((c) => !/[\s-]/.test(c))
    .map((c) => (c === "I" || c === "L" ? "1" : c === "O" ? "0" : c));
  if (symbols.length !== 28) return null;
  const values = symbols.map((c) => SYMBOLS.indexOf(c));
  if (values.some((v) => v < 0)) return null;
  const bit = (i: number) => (values[Math.floor(i / 5)] >> (4 - (i % 5))) & 1;
  const secret = new Uint8Array(16);
  for (let b = 0; b < 16; b++) {
    let v = 0;
    for (let j = 0; j < 8; j++) v = (v << 1) | bit(8 * b + j);
    secret[b] = v;
  }
  const check = await sha256(secret);
  for (let i = 0; i < 12; i++) if (bit(128 + i) !== bitOf(check, i)) return null;
  return secret;
}

/** The key of `secret`. */
export async function keyOf(secret: Uint8Array): Promise<Key> {
  const seed = await sha256(concat(text("xqmeet identity"), secret));
  const pkcs8 = concat(Uint8Array.from(PKCS8), seed);
  // Extractable: its public half comes out of its JWK.
  const key = await crypto.subtle.importKey("pkcs8", pkcs8 as BufferSource, { name: "Ed25519" }, true, ["sign"]);
  const jwk = await crypto.subtle.exportKey("jwk", key);
  return {
    key: fromBase64url(jwk.x!),
    sign: async (data) =>
      new Uint8Array(await crypto.subtle.sign({ name: "Ed25519" }, key, data as BufferSource)),
  };
}

/** What a hello signs: "xqmeet hello", a line feed, the connection's challenge (protocol.helloText). */
export function helloText(nonce: Uint8Array): Uint8Array {
  return concat(text("xqmeet hello\n"), nonce);
}
